import { useEffect, useState } from 'react'
import { observer } from 'mobx-react'
import { Col, Divider, Row, Space, Typography, Button, Spin } from 'antd'
import { useParams, Link } from 'react-router-dom'
import Page from './Page'
import { useStores } from '../stores'
import { collections, INFTCollection } from '../stores/collections'
import { Metrics } from '../components/Metrics'
import { INFT } from '../utils/types'
import { NFTCard } from '../components/NFTCard'

const { Title, Text } = Typography

interface IParams {
  address: string
}

const PER_PAGE = 24
const BATCH_SIZE = 100

const CollectionPage = observer(() => {
  const { store, configProvider } = useStores()

  const params: IParams = useParams() as unknown as IParams
  const collectionAddress = params.address
  const collection: INFTCollection | undefined = collections[configProvider.getChainConfig().chainId].find((c) => c.address === collectionAddress)
  const chainInfo = store.chainInfo

  const [isLoading, setIsLoading] = useState(false)
  const [nfts, setNfts] = useState([] as INFT[])
  const [page, setPage] = useState(1)
  const [hasMore, setHasMore] = useState(false)
  const [totalSuply, setTotalSupply] = useState(0)
  const [maxSupply, setMaxSupply] = useState(0)

  const fetchNfts = async (page = 1, perPage = PER_PAGE): Promise<void> => {
    setIsLoading(true)
    const web3 = store.getKeyProvider().getWeb3()
    const contract = new web3.eth.Contract(collection?.abi, collection?.address)
    const [items, hasMore] = await store.getMintEvents(contract, collection, page, perPage, BATCH_SIZE)
    setPage(page)
    setNfts([...nfts, ...items])
    setHasMore(hasMore)
    setIsLoading(false)
  }

  const fetchMore = (): void => {
    if (isLoading) return
    void fetchNfts(page + 1)
  }

  useEffect(() => {
    if (collection === undefined) return
    if (chainInfo.blockNumber === 1) return

    const fetch = async (): Promise<void> => {
      const [total, max] = await store.fetchSupplyInfo(collection.address)
      setTotalSupply(total)
      setMaxSupply(max)
    }
    void fetch()
    void fetchNfts()
  }, [collection, chainInfo])

  if (!store.isConnected) {
    return (<SimplePage message={'Not Connected'} />)
  }

  if (collection === undefined) {
    return (<SimplePage message={`Unkwown Collection ${collectionAddress}`} />)
  }

  return (
    <Page className="pd-10 collection">
      <Space direction="vertical" size="middle" style={{ display: 'flex' }}>
        <Row>
          <Col span={24}>
            <Title className="page-title" level={1}>{collection?.displayName}</Title>
            <Divider />
          </Col>
        </Row>
        <Row>
          <Col lg={12} sm={24} xs={24} md={24}>
            <Metrics firstValue={totalSuply} secondValue={maxSupply} firstTitle={'TOTAL MINTED'} secondTitle={'TOTAL SUPPLY'} />
          </Col>
        </Row>

        {(nfts.length === 0) && !isLoading && (<Row justify='center'><Text>{'There are no NFTs minted yet'}</Text></Row>)}

        <Row align="top">
          {nfts.map(nft => (
            <Col lg={4} sm={24} xs={24} md={24} key={nft.tokenId}>
              <Link to={`/nfts/${collection.address}/${nft.tokenId}`} state={{ from: { path: `/collection/${collection.address}`, name: collection.displayName } }}>
                <NFTCard title={`${collection.displayName} #${nft.tokenId}`} cover={nft.image} />
              </Link>
            </Col>
          ))}
        </Row>
        {isLoading && (
          <Row justify="center">
            <Col lg={3} sm={24} xs={24} md={24} key={'loader'} >
              <div className='collection-loader'>Loading... <Spin /></div>
            </Col>
          </Row>
        )}
        {!isLoading && hasMore && (
          <Row>
            <Button onClick={() => fetchMore()}>{'More...'}</Button>
          </Row>
        )}
      </Space>
    </Page>
  )
})

const SimplePage = ({ message }: { message: string }): JSX.Element => {
  return (<Page className={'pd-10'}><Title>{message}</Title></Page>)
}

export default CollectionPage
